import i18n from "i18next";
import { initReactI18next } from "react-i18next";

import de from "./de.json";
import en from "./en.json";
import { assertI18nParityDev } from "../utils/i18nParity";

export const SUPPORTED_LANGUAGES = ["de", "en"];
export const DEFAULT_LANGUAGE = "de";

/**
 * Language storage semantics (locked in Phase 3.6):
 *
 *   Persisted language  = `user.language` on the backend (Profile dropdown).
 *   Session language    = per-tab override, sessionStorage key below.
 *
 * A fresh browser has no sessionStorage entry, so the app boots in DEFAULT_LANGUAGE (de).
 * After login, AuthProvider aligns i18n to `user.language`.
 * The header flag chip only writes to sessionStorage (no backend call).
 * The Profile dropdown writes to the backend and CLEARS sessionStorage.
 */
const SESSION_STORAGE_KEY = "mh_session_lang";

function readInitialLanguage() {
  try {
    const stored = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (stored && SUPPORTED_LANGUAGES.includes(stored)) return stored;
  } catch {
    /* sessionStorage may be unavailable — fall through */
  }
  return DEFAULT_LANGUAGE;
}

i18n.use(initReactI18next).init({
  resources: {
    de: { translation: de },
    en: { translation: en },
  },
  lng: readInitialLanguage(),
  fallbackLng: DEFAULT_LANGUAGE,
  // Single translation namespace — disable the ":" ns separator so keys like
  // "orders.sort.created_at:desc" resolve correctly.
  nsSeparator: false,
  interpolation: { escapeValue: false },
  returnNull: false,
});

/** Persist a session-only language override (used by the header flag chip). */
export function setSessionLanguage(lang) {
  try {
    window.sessionStorage.setItem(SESSION_STORAGE_KEY, lang);
  } catch {
    /* ignore */
  }
}

/** Clear the session-only override (used after a Profile save). */
export function clearSessionLanguage() {
  try {
    window.sessionStorage.removeItem(SESSION_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/** Read the current session-only override (or null if none). */
export function getSessionLanguage() {
  try {
    const v = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (v && SUPPORTED_LANGUAGES.includes(v)) return v;
  } catch {
    /* ignore */
  }
  return null;
}

/** Change locale WITHOUT touching sessionStorage. */
export function changeLanguage(lang) {
  if (!SUPPORTED_LANGUAGES.includes(lang)) return;
  i18n.changeLanguage(lang);
  document.documentElement.lang = lang;
}

/** Change locale AND persist to sessionStorage — header flag chip helper. */
export function setSessionAndChangeLanguage(lang) {
  if (!SUPPORTED_LANGUAGES.includes(lang)) return;
  setSessionLanguage(lang);
  changeLanguage(lang);
}

// Ensure <html lang> is set on load.
document.documentElement.lang = i18n.language || DEFAULT_LANGUAGE;

// Dev-only console warning if DE / EN drift.
assertI18nParityDev(de, en);

export default i18n;
